import React, { useEffect, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Checkbox, Snackbar, Alert,
  Chip, Tooltip, Card, CardContent, Stack, Divider, Badge,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import LockIcon from "@mui/icons-material/Lock";
import SecurityIcon from "@mui/icons-material/Security";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  fetchRoles, fetchPrivileges, createRole, updateRole, deleteRole,
  IRole, IRolePrivilege, IPrivilege,
} from "../../../Api/rbacApi";
import { Loader } from "../../../Components/Loader";

const ACTIONS = ["can_view", "can_create", "can_edit", "can_delete"] as const;
const ACTION_LABELS: Record<string, string> = {
  can_view: "View", can_create: "Create", can_edit: "Edit", can_delete: "Delete",
};
const ACTION_COLORS: Record<string, string> = {
  can_view: "#2196f3", can_create: "#4caf50", can_edit: "#ff9800", can_delete: "#f44336",
};

const UserRoleList: React.FC = () => {
  const [roles, setRoles] = useState<IRole[]>([]);
  const [modules, setModules] = useState<IPrivilege[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRole, setEditRole] = useState<IRole | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [perms, setPerms] = useState<Record<string, Record<string, boolean>>>({});
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" as "success" | "error" });
  const [deleteConfirm, setDeleteConfirm] = useState<IRole | null>(null);

  const loadData = async () => {
    setLoading(true);
    const [r, m] = await Promise.all([fetchRoles(), fetchPrivileges()]);
    setRoles(r);
    setModules(m);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const openCreate = () => {
    setEditRole(null);
    setName("");
    setDescription("");
    const emptyPerms: Record<string, Record<string, boolean>> = {};
    modules.forEach((m) => {
      emptyPerms[m.module] = { can_view: false, can_create: false, can_edit: false, can_delete: false };
    });
    setPerms(emptyPerms);
    setDialogOpen(true);
  };

  const openEdit = (role: IRole) => {
    setEditRole(role);
    setName(role.name);
    setDescription(role.description || "");
    const p: Record<string, Record<string, boolean>> = {};
    modules.forEach((m) => {
      const rp = role.privileges.find((pr) => pr.module === m.module);
      p[m.module] = {
        can_view: rp?.can_view ?? false,
        can_create: rp?.can_create ?? false,
        can_edit: rp?.can_edit ?? false,
        can_delete: rp?.can_delete ?? false,
      };
    });
    setPerms(p);
    setDialogOpen(true);
  };

  const togglePerm = (module: string, action: string) => {
    setPerms((prev) => ({
      ...prev,
      [module]: { ...prev[module], [action]: !prev[module]?.[action] },
    }));
  };

  const toggleAll = (action: string) => {
    const allChecked = modules.every((m) => perms[m.module]?.[action]);
    setPerms((prev) => {
      const updated = { ...prev };
      modules.forEach((m) => {
        updated[m.module] = { ...updated[m.module], [action]: !allChecked };
      });
      return updated;
    });
  };

  const toggleRow = (module: string) => {
    const allChecked = ACTIONS.every((a) => perms[module]?.[a]);
    setPerms((prev) => ({
      ...prev,
      [module]: { can_view: !allChecked, can_create: !allChecked, can_edit: !allChecked, can_delete: !allChecked },
    }));
  };

  const handleSave = async () => {
    const privileges: IRolePrivilege[] = modules.map((m) => ({
      module: m.module,
      can_view: perms[m.module]?.can_view ?? false,
      can_create: perms[m.module]?.can_create ?? false,
      can_edit: perms[m.module]?.can_edit ?? false,
      can_delete: perms[m.module]?.can_delete ?? false,
    }));

    try {
      if (editRole) {
        await updateRole(editRole.id, { name, description, privileges });
        setSnack({ open: true, msg: "Role updated successfully", severity: "success" });
      } else {
        await createRole({ name, description, privileges });
        setSnack({ open: true, msg: "Role created successfully", severity: "success" });
      }
      setDialogOpen(false);
      loadData();
    } catch (e: any) {
      setSnack({ open: true, msg: e.message || "Error", severity: "error" });
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteRole(deleteConfirm.id);
      setSnack({ open: true, msg: "Role deleted successfully", severity: "success" });
      setDeleteConfirm(null);
      loadData();
    } catch (e: any) {
      setSnack({ open: true, msg: e.message || "Error", severity: "error" });
    }
  };

  const getPermCount = (role: IRole) => {
    return role.privileges.reduce((acc, p) => {
      return acc + (p.can_view ? 1 : 0) + (p.can_create ? 1 : 0) + (p.can_edit ? 1 : 0) + (p.can_delete ? 1 : 0);
    }, 0);
  };

  if (loading) return <Loader height="60vh" />;

  const moduleOrderMap: Record<string, number> = {};
  modules.forEach((m, i) => { moduleOrderMap[m.module] = i; });

  return (
    <Box p={3}>
      {/* Header */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, background: "linear-gradient(135deg, #1a3353 0%, #2d5f8a 100%)", color: "#fff" }}>
        <CardContent sx={{ py: 3, px: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" gap={2}>
              <SecurityIcon sx={{ fontSize: 36, opacity: 0.9 }} />
              <Box>
                <Typography variant="h5" fontWeight={700}>User Roles</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Define roles and assign module permissions</Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{
                bgcolor: "#D4AF37",
                color: "#1a3353",
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                "&:hover": { bgcolor: "#b89540" },
              }}
            >
              Add Role
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Role Cards */}
      <Stack gap={2}>
        {roles.map((role) => {
          const activeModules = role.privileges
            .filter((p) => p.can_view || p.can_create || p.can_edit || p.can_delete)
            .sort((a, b) => (moduleOrderMap[a.module] ?? 999) - (moduleOrderMap[b.module] ?? 999));
          return (
            <Card key={role.id} elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8e8e8", "&:hover": { borderColor: "#D4AF37", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }, transition: "all 0.2s" }}>
              <CardContent sx={{ py: 2.5, px: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="h6" fontWeight={700} color="#1a3353">{role.name}</Typography>
                      {role.is_system && (
                        <Tooltip title="System role - cannot be deleted">
                          <LockIcon sx={{ fontSize: 16, color: "#D4AF37" }} />
                        </Tooltip>
                      )}
                      <Chip
                        icon={<GroupIcon sx={{ fontSize: "14px !important" }} />}
                        label={`${role.users_count ?? 0} users`}
                        size="small"
                        sx={{ ml: 1, fontSize: 11, height: 24, bgcolor: "#f0f4f8", color: "#1a3353", fontWeight: 600 }}
                      />
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                        label={`${getPermCount(role)} permissions`}
                        size="small"
                        sx={{ fontSize: 11, height: 24, bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mb={1.5}>{role.description}</Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {activeModules.map((p) => {
                        const perms = [
                          p.can_view && "V",
                          p.can_create && "C",
                          p.can_edit && "E",
                          p.can_delete && "D",
                        ].filter(Boolean).join("");
                        return (
                          <Tooltip key={p.module} title={`${p.module.replace(/_/g, " ")}: ${[p.can_view && "View", p.can_create && "Create", p.can_edit && "Edit", p.can_delete && "Delete"].filter(Boolean).join(", ")}`}>
                            <Chip
                              label={
                                <span>
                                  {p.module.replace(/_/g, " ")}
                                  <span style={{ opacity: 0.5, marginLeft: 4, fontSize: 10 }}>{perms}</span>
                                </span>
                              }
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 12, height: 26, borderColor: "#d0d7de", color: "#1a3353", textTransform: "capitalize" }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                  <Stack direction="row" gap={0.5} ml={2}>
                    <IconButton size="small" onClick={() => openEdit(role)} sx={{ bgcolor: "#f0f4f8", color: "#1a3353", "&:hover": { bgcolor: "#e0e7ef" } }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {!role.is_system && (
                      <IconButton size="small" onClick={() => setDeleteConfirm(role)} sx={{ bgcolor: "#fef2f2", color: "#d32f2f", "&:hover": { bgcolor: "#fee2e2" } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: "#1a3353", color: "#fff", fontWeight: 700 }}>
          {editRole ? `Edit Role: ${editRole.name}` : "Create New Role"}
        </DialogTitle>
        <DialogContent sx={{ pt: "24px !important" }}>
          <Stack direction="row" gap={2} mb={3}>
            <TextField label="Role Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required variant="outlined" />
            <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
          </Stack>

          <Typography variant="subtitle1" fontWeight={700} color="#1a3353" mb={1}>Permission Matrix</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: 700, color: "#1a3353", width: 200 }}>Module</TableCell>
                  {ACTIONS.map((a) => (
                    <TableCell key={a} align="center" sx={{ fontWeight: 700, width: 90 }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap={0.3}>
                        <Typography variant="caption" fontWeight={700} sx={{ color: ACTION_COLORS[a] }}>
                          {ACTION_LABELS[a]}
                        </Typography>
                        <Checkbox
                          size="small"
                          checked={modules.every((m) => perms[m.module]?.[a])}
                          indeterminate={modules.some((m) => perms[m.module]?.[a]) && !modules.every((m) => perms[m.module]?.[a])}
                          onChange={() => toggleAll(a)}
                          sx={{ p: 0.3, color: ACTION_COLORS[a], "&.Mui-checked": { color: ACTION_COLORS[a] } }}
                        />
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 700, width: 70 }}>
                    <Typography variant="caption" fontWeight={700} color="#1a3353">All</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((m, i) => (
                  <TableRow key={m.module} sx={{ bgcolor: i % 2 === 0 ? "#fff" : "#fafbfc", "&:hover": { bgcolor: "#f0f7ff" } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ textTransform: "capitalize" }}>
                        {m.display_name}
                      </Typography>
                    </TableCell>
                    {ACTIONS.map((a) => (
                      <TableCell key={a} align="center">
                        <Checkbox
                          size="small"
                          checked={perms[m.module]?.[a] ?? false}
                          onChange={() => togglePerm(m.module, a)}
                          sx={{ p: 0.3, color: ACTION_COLORS[a], "&.Mui-checked": { color: ACTION_COLORS[a] } }}
                        />
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Checkbox
                        size="small"
                        checked={ACTIONS.every((a) => perms[m.module]?.[a])}
                        indeterminate={ACTIONS.some((a) => perms[m.module]?.[a]) && !ACTIONS.every((a) => perms[m.module]?.[a])}
                        onChange={() => toggleRow(m.module)}
                        sx={{ p: 0.3 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: "#666" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!name.trim()}
            sx={{ bgcolor: "#1a3353", borderRadius: 2, px: 4, "&:hover": { bgcolor: "#2d5f8a" } }}
          >
            {editRole ? "Update Role" : "Create Role"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>?</Typography>
          {(deleteConfirm?.users_count ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
              This role has {deleteConfirm?.users_count} user(s) assigned. They will lose their permissions.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ color: "#666" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} sx={{ borderRadius: 2 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={snack.severity} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default UserRoleList;
